import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { ProductsUnavailableState } from '~/features/deliveries/components/products-unavailable-state';
import { useDeliveryRunDetail } from '~/features/deliveries/hooks/use-delivery-run-detail';
import {
  productsUnavailableOf,
  useDeliveryRunProducts,
} from '~/features/deliveries/hooks/use-delivery-run-products';
import {
  FALLBACK_PRODUCT_OUTCOMES,
  defaultProductOutcome,
  useProductOutcomes,
} from '~/features/deliveries/hooks/use-product-outcomes';
import type {
  ProductOutcomeQuery,
  RunProductsQuery,
} from '~/features/deliveries/types/delivery-runs.types';
import { resolveProductsUnavailable } from '~/features/deliveries/utils/products-unavailable';

import { useListView } from '../hooks/use-list-view';
import { useRunLabels } from '../hooks/use-run-labels';
import { useDeliveryOutcomeParam } from '../utils/delivery-outcome-context';
import { DENSITY } from '../utils/list-density';
import { BOUNDED_LIST_CLASS, DASH, sourceIdSummary, STICKY_HEAD_CLASS } from '../utils/run-format';
import { parseSourceProductId } from '../utils/source-product-id';

import { DeliveryRetryFailedButton } from './delivery-retry-failed-button';
import { GateContentUnavailable } from './gate-content-unavailable';
import { ListViewControls } from './list-view-controls';
import type { ProductCardItem } from './product-card-grid';
import { ProductCardGrid } from './product-card-grid';
import { ProductCell } from './product-cell';
import { RunStatusChip } from './run-status-chip';

/** Neither null nor the raw connector path belongs as a title — Q17/18: fall back to
 *  the parsed offer id when integrations stored no payload title. */
function productTitle(row: {
  title: string | null;
  externalId: string | null;
  sku: string;
}): string {
  return row.title ?? parseSourceProductId(row.externalId ?? row.sku)?.offerId ?? row.sku;
}

interface Props {
  /** `progress.delivery.externalJobRef` — integrations' own id for this delivery push. */
  externalJobRef: string | null | undefined;
}

/**
 * The outcome menu's query as a `RunProductsQuery`. Built with conditional
 * spreads (not `field: value ?? undefined`) because `exactOptionalPropertyTypes`
 * treats an explicit `undefined` as different from an absent key.
 */
function toRunProductsQuery(query: ProductOutcomeQuery | undefined): RunProductsQuery {
  if (!query) return {};
  const { outcome, outcomes, excludeOutcomes } = query;
  return {
    ...(outcome !== undefined ? { outcome } : {}),
    ...(outcomes !== undefined
      ? { outcomes: Array.isArray(outcomes) ? outcomes : [outcomes] }
      : {}),
    ...(excludeOutcomes !== undefined
      ? { excludeOutcomes: Array.isArray(excludeOutcomes) ? excludeOutcomes : [excludeOutcomes] }
      : {}),
  };
}

/**
 * P10b per-product delivery results, owned by agentic-integrations: a page of
 * this delivery's product ledger (sync outcome, error) filtered by the same
 * outcome menu the Deliveries feature uses. Product column is `ProductCell`
 * (Q17/18 — no separate sku/id column; brand fills line 2) and the outcome is a
 * semantic chip (Q19). Reuses its existing read (`getDeliveryRunProducts`) and
 * query key rather than re-implementing one — integrations is a separate
 * service, so a failed or 404 read here never blocks the rest of the approval
 * page.
 */
export function DeliveryRunProducts({ externalJobRef }: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { statusLabel } = useRunLabels();
  // The outcome filter lives in the route's URL (`dout`) where the route provides it.
  const urlOutcome = useDeliveryOutcomeParam();
  const [localOutcome, setLocalOutcome] = useState<string | null>(null);
  const outcomeId = urlOutcome ? urlOutcome.value : localOutcome;
  const setOutcomeId = urlOutcome ? urlOutcome.onChange : setLocalOutcome;
  const { view, density, setView, setDensity } = useListView('delivery-products');
  const outcomesQuery = useProductOutcomes();
  const outcomeOptions = outcomesQuery.data ?? FALLBACK_PRODUCT_OUTCOMES;
  const selected =
    outcomeOptions.find((o) => o.id === outcomeId) ?? defaultProductOutcome(outcomeOptions);

  const productsQuery = useDeliveryRunProducts(
    externalJobRef ?? '',
    toRunProductsQuery(selected?.query),
    !!externalJobRef,
  );
  // The run itself lets the empty state name a run that failed before any product
  // (older servers call it "not tracked"). Shares the Retry-failed button's cache
  // entry; no polling — the inference only applies once the run is terminal.
  const runQuery = useDeliveryRunDetail(externalJobRef ?? '', !!externalJobRef, { poll: false });

  if (!externalJobRef) {
    return (
      <section aria-label={t('delivery.products.title')} className="flex flex-col gap-2">
        <h4 className="text-foreground text-sm font-semibold">{t('delivery.products.title')}</h4>
        <p className="text-muted-foreground text-sm">{t('delivery.products.noJob')}</p>
      </section>
    );
  }

  const rows = productsQuery.data?.pages.flatMap((page) => page.products) ?? [];
  // R6: no all-empty column — "Error" only when some row has one.
  const anyError = rows.some((row) => !!(row.reasonLabel ?? row.errorMessage));
  // An empty first unfiltered page says WHY there are no rows (failed early / never
  // tracked / aged out) as a compact state card; no actions outside the console.
  const unavailable =
    rows.length === 0
      ? resolveProductsUnavailable(productsUnavailableOf(productsQuery.data), runQuery.data, {
          unfiltered: selected?.id === defaultProductOutcome(outcomeOptions)?.id,
        })
      : null;

  return (
    <section aria-label={t('delivery.products.title')} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* R7: the count sits in the title; "shown" while more pages remain to load. */}
        <h4 className="text-foreground text-sm font-semibold">
          {rows.length > 0
            ? t(
                productsQuery.hasNextPage
                  ? 'delivery.products.titleShown'
                  : 'delivery.products.titleCount',
                { count: rows.length },
              )
            : t('delivery.products.title')}
        </h4>
        <div className="flex items-center gap-2">
          <DeliveryRetryFailedButton deliveryRunId={externalJobRef} />
          <Select value={selected?.id ?? ''} onValueChange={setOutcomeId}>
            <SelectTrigger
              className="h-8 w-52 cursor-pointer text-xs"
              aria-label={t('delivery.products.filter')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {outcomeOptions.map((option) => (
                <SelectItem key={option.id} value={option.id} className="cursor-pointer">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ListViewControls
            view={view}
            density={density}
            onViewChange={setView}
            onDensityChange={setDensity}
          />
        </div>
      </div>

      {productsQuery.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : productsQuery.isError ? (
        <GateContentUnavailable what={t('delivery.products.title')} />
      ) : unavailable ? (
        <ProductsUnavailableState info={unavailable} run={runQuery.data} compact />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('delivery.products.empty')}</p>
      ) : view === 'cards' ? (
        <div className={BOUNDED_LIST_CLASS}>
          <ProductCardGrid
            density={density}
            items={rows.map(
              (row, index): ProductCardItem => ({
                key: `${row.sku}-${String(index)}`,
                title: productTitle(row),
                imageUrl: row.imageUrl,
                // Q19: outcome as a semantic chip (icon + human label), never raw text.
                badges: (
                  <RunStatusChip status={row.outcome} label={statusLabel(row.outcome)} showIcon />
                ),
                subtitle:
                  row.reasonLabel ?? row.errorMessage ?? sourceIdSummary([row.externalId, row.sku]),
              }),
            )}
          />
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table containerClassName={BOUNDED_LIST_CLASS}>
              <TableHeader className={STICKY_HEAD_CLASS}>
                <TableRow>
                  {/* Q17/Q22: no separate sku/id column — the product cell carries it and
                      takes the free width; brand fills the row instead of empty space. */}
                  <TableHead>{t('delivery.products.product')}</TableHead>
                  <TableHead className="w-px whitespace-nowrap">
                    {t('delivery.products.outcome')}
                  </TableHead>
                  {anyError ? <TableHead>{t('delivery.products.error')}</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.sku}-${String(index)}`}>
                    <TableCell className={DENSITY[density].rowClass}>
                      <ProductCell
                        title={productTitle(row)}
                        imageUrl={row.imageUrl}
                        sourceProductIds={[row.externalId, row.sku]}
                        meta={row.brand}
                        density={density}
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <RunStatusChip
                        status={row.outcome}
                        label={statusLabel(row.outcome)}
                        showIcon
                      />
                    </TableCell>
                    {anyError ? (
                      <TableCell className="text-muted-foreground max-w-96 text-sm break-words">
                        {row.reasonLabel ?? row.errorMessage ?? DASH}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {productsQuery.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer self-start"
              onClick={() => {
                void productsQuery.fetchNextPage();
              }}
              disabled={productsQuery.isFetchingNextPage}
              isLoading={productsQuery.isFetchingNextPage}
            >
              {t('delivery.products.loadMore')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}
